import React, { useRef } from "react";
import { Check } from "lucide-react";
import { useNow } from "../hooks/useNow";

export type StageStatus = "pending" | "active" | "done";

export interface TimelineStage {
  key: string;
  label: string;
  status: StageStatus;
  /** Who is working right now, e.g. "Logician and Statistician writing". */
  activity?: string;
}

function formatDuration(ms: number) {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

/**
 * Vertical progress rail for a running council. Timings are only shown for
 * stages this browser watched start, so a debate opened from history never
 * displays invented durations.
 */
export default function DebateTimeline({
  stages,
  isLoading,
  title = "Council progress",
}: {
  stages: TimelineStage[];
  isLoading: boolean;
  title?: string;
}) {
  // Stages run in order, so while the council is working and the backend has
  // not reported the next stage yet, that stage is the one being prepared.
  if (isLoading && !stages.some((s) => s.status === "active")) {
    const next = stages.findIndex((s) => s.status === "pending");
    if (next !== -1) {
      stages = stages.map((s, i) => (i === next ? { ...s, status: "active", activity: "In progress" } : s));
    }
  }
  const startedAt = useRef<Record<string, number>>({});
  const finishedAt = useRef<Record<string, number>>({});
  const watched = useRef<Set<string>>(new Set());
  const now = useNow(1000, isLoading);

  for (const stage of stages) {
    if (stage.status === "active" && isLoading && !startedAt.current[stage.key]) {
      startedAt.current[stage.key] = Date.now();
      watched.current.add(stage.key);
    }
    if (stage.status === "done" && watched.current.has(stage.key) && !finishedAt.current[stage.key]) {
      finishedAt.current[stage.key] = Date.now();
    }
  }

  const doneCount = stages.filter((s) => s.status === "done").length;
  const active = stages.find((s) => s.status === "active");

  return (
    <section className="debate-timeline" aria-label={title}>
      <div className="debate-timeline-header">
        <h2>{title}</h2>
        <span className="font-mono tabular-nums">
          {doneCount} / {stages.length}
        </span>
      </div>
      <p className="sr-only" aria-live="polite">
        {active ? `${active.label} in progress` : doneCount === stages.length ? "All stages complete" : ""}
      </p>
      <ol>
        {stages.map((stage, i) => {
          const start = startedAt.current[stage.key];
          const end = finishedAt.current[stage.key];
          const timing =
            stage.status === "active" && start
              ? formatDuration(now - start)
              : stage.status === "done" && start && end
                ? formatDuration(end - start)
                : null;
          const isActive = stage.status === "active" && isLoading;
          return (
            <li key={stage.key} className={`timeline-stage is-${isActive ? "active" : stage.status === "active" ? "pending" : stage.status}`}>
              <span className="timeline-node" aria-hidden="true">
                {stage.status === "done" ? <Check size={11} strokeWidth={3} className="motion-check" /> : null}
              </span>
              {i < stages.length - 1 && (
                <span className="timeline-segment" aria-hidden="true">
                  <span className={stage.status === "done" ? "is-filled" : ""} />
                </span>
              )}
              <div className="timeline-copy">
                <span className="timeline-label">{stage.label}</span>
                {timing && <span className="timeline-time font-mono tabular-nums">{timing}</span>}
                {isActive && stage.activity && <span className="timeline-activity">{stage.activity}</span>}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
